/**
 * CreateRunSection — Wizard-Orchestrator: 3 Schritte (Channels → Texttypen &
 * Vorlagen → Kosten-Vorschau), Spec 2026-07-24 §4.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Stepper } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'

import { useFieldRecipes } from '../hooks/useFieldRecipes'
import { StepChannels } from './StepChannels'
import { StepChannelFields } from './StepChannelFields'
import { StepCostPreview } from './StepCostPreview'
import { useWizardActions } from './useWizardActions'
import { useWizardState } from './useWizardState'
import { WizardFooterNav } from './WizardFooterNav'
import { buildRecipeDefaultsByChannel } from './buildRecipeDefaults'

interface CreateRunSectionProps {
  productIds: string[]
  initialChannelIds?: string[]
}

export function CreateRunSection({ productIds, initialChannelIds }: CreateRunSectionProps) {
  const { t } = useTranslation()
  const wizard = useWizardState(initialChannelIds)
  const { goNext, handleStarted } = useWizardActions(wizard)
  const { data: fieldRecipes = [] } = useFieldRecipes()
  const recipeDefaultsByChannel = useMemo(
    () => buildRecipeDefaultsByChannel(fieldRecipes),
    [fieldRecipes],
  )

  const steps = [
    { label: t('channelContent.wizard.stepChannels') },
    { label: t('channelContent.wizard.stepFields') },
    { label: t('channelContent.wizard.stepCost') },
  ]

  return (
    <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
      <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
        {t('channelContent.wizard.title')}
      </h2>
      {productIds.length > 0 && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {t('channelContent.wizard.productsSelected', { count: productIds.length })}
        </p>
      )}

      <div id={EID['text-bulk-wizard-stepper-region']}>
        <Stepper steps={steps} currentStep={wizard.step} />
      </div>

      {wizard.step === 0 && (
        <StepChannels
          productIds={productIds}
          selectedChannelIds={wizard.selectedChannelIds}
          onToggleChannel={(channelId) => wizard.toggleChannel(channelId, recipeDefaultsByChannel[channelId])}
          researchEnabled={wizard.researchEnabled}
          onResearchChange={wizard.setResearchEnabled}
        />
      )}
      {wizard.step === 1 && (
        <StepChannelFields
          selectedChannelIds={wizard.selectedChannelIds}
          fieldSelections={wizard.fieldSelections}
          onFieldChange={wizard.setFieldSelection}
          productIds={productIds}
        />
      )}
      {wizard.step === 2 && (
        <StepCostPreview
          productIds={productIds}
          selectedChannelIds={wizard.selectedChannelIds}
          fieldSelections={wizard.fieldSelections}
          researchEnabled={wizard.researchEnabled}
          onStarted={handleStarted}
        />
      )}

      <WizardFooterNav
        step={wizard.step}
        onBack={() => wizard.setStep(wizard.step - 1)}
        onNext={goNext}
        nextDisabled={wizard.step === 0 && wizard.selectedChannelIds.length === 0}
      />
    </div>
  )
}
